import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SectionHeading } from 'components/molecules';
import { Button, FieldCheckbox, Icon, Loading, Typography } from 'components/atoms';
import {
  CbiSectionHeader,
  BulkUploadActionContainer,
  DocumentsContainer,
  DocumentsPlaceholder,
  DocumentErrorCount,
  IconButtonWithLabel,
  RecordsLabel,
  RecordsSummary,
  PendingUploadDeleteButton,
} from './Documents.styles';
import { FileFormat, FileInfo, getFileExtension } from 'helpers/documentHelper';
import { useDownloadState, useFetch, useSignalR } from 'hooks';
import { useAuth } from 'auth';
import { useConfig } from 'config';
import { useTerminologyConfig } from 'context/DataContext';
import { GatewayPermissions } from 'auth/constants/permissions';
import DocumentUploadModal from 'components/organisms/document-upload-modal';
import { ToastContext, ToastContextProps } from 'toast';
import { ToastType } from 'components/molecules/toast/types';
import { ReactComponent as DocumentsPlaceholderIcon } from './../../../../assets/svg/Icons/DocumentsPlaceholder.svg';
import DeleteLinkedConfirmationModal from 'components/organisms/delete-linked-confirmation-modal';
import ConfirmationModal from 'components/organisms/confirmation-modal';
import DocumentCard from 'components/molecules/document-card';
import ThreeDotsContextMenu from 'components/organisms/three-dots-context-menu/ThreeDotsContextMenu';
import { useUserSettings } from 'context/DataContext';
import { FileInformationResponse, ThirdPartyType } from 'types';
import { ApiError } from 'components/organisms/three-dots-context-menu/types';
import { decodedFileName } from 'helpers/strings-functions';
import DocumentBulkFileInput, {
  PendingDocumentUpload,
} from 'components/organisms/document-bulk-file-input/DocumentBulkFileInput';

interface DocumentsProps {
  thirdPartyId: string;
  type: ThirdPartyType;
}

interface FileStatusSignalRMessage {
  id?: string;
  status?: string;
  errorMessage?: string | null;
}

interface FileStatusUpdate {
  status: string;
  errorMessage?: string | null;
}

const Documents: FC<DocumentsProps> = ({ thirdPartyId, type }) => {
  const { tokenInfo } = useAuth();
  const { hasPermission, email, clientId, isEnhancedDocumentUploadControlsEnabled } =
    useUserSettings();
  const terminologyConfig = useTerminologyConfig();
  const { vantageWebApi } = useConfig();
  const [isDocumentUploadModalOpen, setIsDocumentUploadModalOpen] = useState({
    open: false,
    documentId: undefined,
  });
  const [areConflictedFilesModalOpen, setAreConflictedFilesModalOpen] = useState({
    open: false,
    conflictedScreenings: undefined,
  });
  const { openToast } = useContext<ToastContextProps>(ToastContext);
  const [isMultiSelect, setIsMultiSelect] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [pendingDocumentUploads, setPendingDocumentUploads] = useState<PendingDocumentUpload[]>([]);
  const [locallyUploadedFiles, setLocallyUploadedFiles] = useState<FileInformationResponse[]>([]);
  const [fileStatusMessage, setFileStatusMessage] = useState<FileStatusSignalRMessage | null>(null);
  const [fileStatusUpdates, setFileStatusUpdates] = useState<Record<string, FileStatusUpdate>>({});
  const [dataGetThirdPartyFiles, setDataGetThirdPartyFiles] = useState<
    FileInformationResponse[] | null
  >(null);
  const pendingDocumentUploadsCountRef = useRef(0);
  const shouldReloadAfterPendingUploadsRef = useRef(false);
  const reloadDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const reloadMaxWaitTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const filesRequestSequenceRef = useRef(0);
  const { connectionId: signalRConnectionId } = useSignalR(
    setFileStatusMessage,
    'fileupload',
    clientId
  );
  const {
    downloads,
    resetDownload,
    failDowload,
    progressDownload,
    startDownload,
    successDownload,
  } = useDownloadState();

  const { loading: loadingGetThirdPartyFiles, trigger: fetchThirdPartyFiles } = useFetch<
    FileInformationResponse[]
  >(`${vantageWebApi}/thirdparty/${thirdPartyId}/files`, {
    lazy: true,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo.accessToken}`,
    },
  });

  const reloadGetThirdPartyFiles = useCallback(async () => {
    const requestId = ++filesRequestSequenceRef.current;
    const result = await fetchThirdPartyFiles();

    if (result && requestId === filesRequestSequenceRef.current) {
      setDataGetThirdPartyFiles(result);
    }
  }, [fetchThirdPartyFiles]);

  useEffect(() => {
    reloadGetThirdPartyFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { loading: loadingRequestFiles, trigger: fetchFileLists } = useFetch(
    `${vantageWebApi}/thirdparty/${thirdPartyId}/request-files`,
    {
      body: JSON.stringify(selectedFileIds),
      lazy: true,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenInfo.accessToken}`,
      },
      onSuccess: () => {
        disableMultiFileDownload();
      },
      onError: () => {
        disableMultiFileDownload();
      },
    }
  );

  const mergedFiles = useMemo(() => {
    const knownIds = new Set((dataGetThirdPartyFiles ?? []).map((file) => file.id));
    const notYetFetchedLocalFiles = locallyUploadedFiles.filter((file) => !knownIds.has(file.id));
    return [...(dataGetThirdPartyFiles ?? []), ...notYetFetchedLocalFiles];
  }, [dataGetThirdPartyFiles, locallyUploadedFiles]);

  const isSelectAllDisabled = useMemo(
    () => mergedFiles.length === selectedFileIds?.length,
    [mergedFiles, selectedFileIds]
  );

  const disableMultiFileDownload = () => {
    setOpenModal(false);
    setIsMultiSelect(false);
    setSelectedFileIds([]);
  };

  const canUploadDocuments: boolean = hasPermission(GatewayPermissions.UploadDocuments);

  const canDeleteDocuments: boolean =
    hasPermission(GatewayPermissions.ViewDocuments) &&
    hasPermission(GatewayPermissions.DeleteDocuments);

  const canEditDocuments: boolean =
    hasPermission(GatewayPermissions.ViewDocuments) &&
    hasPermission(GatewayPermissions.EditDocument);

  const filesInfo = mergedFiles.filter((file) => file.isCbiFile === false) as FileInfo[];
  const cbiFilesInfo = mergedFiles.filter((file) => file.isCbiFile === true) as FileInfo[];
  const uploadingPendingDocumentUploads = pendingDocumentUploads.filter(
    (pendingUpload) => !pendingUpload.failed
  );
  const failedPendingDocumentUploads = pendingDocumentUploads.filter(
    (pendingUpload) => pendingUpload.failed
  );
  const visibleFilesCount = filesInfo.length + uploadingPendingDocumentUploads.length;
  const visibleRecordsCount = mergedFiles.length + uploadingPendingDocumentUploads.length;
  const hasVisibleDocuments = visibleRecordsCount > 0 || failedPendingDocumentUploads.length > 0;
  const documentErrorCount =
    mergedFiles.filter(
      (file) =>
        downloads[file.id]?.failed ||
        (fileStatusUpdates[file.id]?.status ?? file.fileStatus)?.toLowerCase() === 'error'
    ).length + failedPendingDocumentUploads.length;
  const filesLimitExceeded = visibleFilesCount >= 60;
  const showBulkDocumentUpload = isEnhancedDocumentUploadControlsEnabled === false;
  const hasInProgressFiles = mergedFiles.some(
    (file) =>
      (fileStatusUpdates[file.id]?.status ?? file.fileStatus)?.toLowerCase() === 'in progress'
  );

  useEffect(() => {
    if (!hasInProgressFiles) {
      return;
    }

    const pollIntervalId = setInterval(() => {
      reloadGetThirdPartyFiles();
    }, 5000);

    return () => clearInterval(pollIntervalId);
  }, [hasInProgressFiles, reloadGetThirdPartyFiles]);

  const reloadFilesAndClearScheduledTimeouts = useCallback(() => {
    clearTimeout(reloadDebounceTimeoutRef.current);
    clearTimeout(reloadMaxWaitTimeoutRef.current);
    reloadDebounceTimeoutRef.current = undefined;
    reloadMaxWaitTimeoutRef.current = undefined;
    reloadGetThirdPartyFiles();
  }, [reloadGetThirdPartyFiles]);

  const scheduleFilesReload = useCallback(() => {
    clearTimeout(reloadDebounceTimeoutRef.current);

    reloadDebounceTimeoutRef.current = setTimeout(reloadFilesAndClearScheduledTimeouts, 750);

    if (!reloadMaxWaitTimeoutRef.current) {
      reloadMaxWaitTimeoutRef.current = setTimeout(reloadFilesAndClearScheduledTimeouts, 3000);
    }
  }, [reloadFilesAndClearScheduledTimeouts]);

  useEffect(() => {
    if (signalRConnectionId) {
      scheduleFilesReload();
    }
  }, [signalRConnectionId, scheduleFilesReload]);

  useEffect(
    () => () => {
      clearTimeout(reloadDebounceTimeoutRef.current);
      clearTimeout(reloadMaxWaitTimeoutRef.current);
    },
    []
  );

  useEffect(() => {
    const documentId = fileStatusMessage?.id;
    const status = fileStatusMessage?.status;
    const errorMessage = fileStatusMessage?.errorMessage;

    if (!documentId || !status) {
      return;
    }

    setFileStatusUpdates((currentUpdates) => ({
      ...currentUpdates,
      [documentId]: {
        status,
        errorMessage,
      },
    }));

    scheduleFilesReload();
  }, [fileStatusMessage, scheduleFilesReload]);

  const removePendingDocumentUpload = (uploadId: string) => {
    setPendingDocumentUploads((currentUploads) =>
      currentUploads.filter((pendingUpload) => pendingUpload.uploadId !== uploadId)
    );
  };

  const handlePendingUploadsChange = (pendingUploadsCountChange: number) => {
    if (pendingUploadsCountChange > 0) {
      shouldReloadAfterPendingUploadsRef.current = true;
    }

    const nextPendingUploadsCount = Math.max(
      pendingDocumentUploadsCountRef.current + pendingUploadsCountChange,
      0
    );

    pendingDocumentUploadsCountRef.current = nextPendingUploadsCount;

    if (nextPendingUploadsCount === 0 && shouldReloadAfterPendingUploadsRef.current) {
      shouldReloadAfterPendingUploadsRef.current = false;
      reloadFilesAndClearScheduledTimeouts();
    }
  };

  const handleFailView = (status) => {
    switch (status) {
      case 401:
        openToast({
          type: ToastType.Error,
          message: `You don't have enough permissions to view documents`,
        });
        break;

      default:
        openToast({
          type: ToastType.Error,
          message: 'An error occurred while downloading the document',
        });
        break;
    }
  };

  const handleFailDownload = (status) => {
    switch (status) {
      case 401:
        openToast({
          type: ToastType.Error,
          message: `You don't have enough permissions to download documents`,
        });
        break;

      default:
        openToast({
          type: ToastType.Error,
          message: 'An error occurred while downloading the document',
        });
        break;
    }
  };

  const handleSuccessDelete = () => {
    openToast({
      type: ToastType.Success,
      message: 'Document has been successfully deleted',
    });
    reloadGetThirdPartyFiles();
  };

  const handleFailDelete = (status: number, _, body: ApiError) => {
    switch (status) {
      case 401:
        openToast({
          type: ToastType.Error,
          message: `You don't have enough permissions to delete documents`,
        });
        break;

      case 409:
        setAreConflictedFilesModalOpen({
          open: true,
          conflictedScreenings: [...body.errors?.conflictedScreenings],
        });
        break;

      default:
        openToast({
          type: ToastType.Error,
          message: 'An error occurred while deleting the document',
        });
        break;
    }
  };

  const handleSelectDocumentChange = (fileInfoId: string) => {
    const index = selectedFileIds.indexOf(fileInfoId);

    if (index >= 0) {
      selectedFileIds.splice(index, 1);
      setSelectedFileIds([...selectedFileIds]);
    } else {
      setSelectedFileIds([...selectedFileIds, fileInfoId]);
    }
  };

  const handleSelectAllClick = () => {
    const allIds = mergedFiles.map((x) => x.id);
    if (allIds.length > selectedFileIds.length) {
      setSelectedFileIds(allIds);
    }
  };

  const ActionComponentCheckbox = ({ id }: { id: string }) => (
    <FieldCheckbox
      spanStyles={{
        height: '25px',
        width: '25px',
        marginTop: '2px',
        marginRight: '2px',
      }}
      ariaLabel="file-check"
      id={id}
      checked={selectedFileIds.includes(id)}
      onChange={() => handleSelectDocumentChange(id)}
    />
  );

  return (
    <>
      <section style={{ margin: 0 }}>
        <SectionHeading text={`${terminologyConfig?.thirdPartySingularNaming} documents`} border>
          <IconButtonWithLabel
            small
            style={{ background: 'transparent', border: 'none' }}
            onClick={() => {
              if (isMultiSelect && selectedFileIds.length > 0) {
                setOpenModal(true);
              } else {
                setIsMultiSelect((x) => !x);
              }
            }}
          >
            <Icon icon="download" size="xxxs" />
            <span style={{ color: '#053747' }}>
              {isMultiSelect ? `Download (${selectedFileIds.length})` : 'Select to download'}
            </span>
          </IconButtonWithLabel>
          {canUploadDocuments && !showBulkDocumentUpload && (
            <IconButtonWithLabel
              small
              secondary
              disabled={filesLimitExceeded}
              onClick={() => {
                disableMultiFileDownload();
                setIsDocumentUploadModalOpen({
                  open: true,
                  documentId: undefined,
                });
              }}
            >
              <Icon icon="plus-circle" size="xxxs" />
              <span>Add document</span>
            </IconButtonWithLabel>
          )}
          {canUploadDocuments && showBulkDocumentUpload && (
            <BulkUploadActionContainer>
              <DocumentBulkFileInput
                documentsCount={visibleFilesCount}
                entityType={type}
                label="Add documents"
                buttonText="Add documents"
                buttonIcon="plus-circle"
                showLabel={false}
                outlineButton={false}
                secondaryButton
                thirdPartyId={thirdPartyId}
                linkToThirdParty
                notifyAnalyst
                showValidationErrorsAsPendingCards
                isBulkUpload
                disabled={filesLimitExceeded}
                onPendingFileUploadsStart={(pendingUploads) =>
                  setPendingDocumentUploads((currentUploads) => [
                    ...currentUploads,
                    ...pendingUploads,
                  ])
                }
                onPendingFileUploadError={(uploadId, errorMessage) => {
                  setPendingDocumentUploads((currentUploads) =>
                    currentUploads.map((pendingUpload) =>
                      pendingUpload.uploadId === uploadId
                        ? { ...pendingUpload, failed: true, errorMessage }
                        : pendingUpload
                    )
                  );
                  openToast({
                    type: ToastType.Error,
                    message: errorMessage,
                  });
                }}
                onPendingUploadsChange={handlePendingUploadsChange}
                onSuccessDocumentUpload={(fileInformation, pendingUpload) => {
                  if (pendingUpload) {
                    removePendingDocumentUpload(pendingUpload.uploadId);
                  }
                  if (fileInformation) {
                    setLocallyUploadedFiles((currentFiles) => [...currentFiles, fileInformation]);
                  }
                }}
              />
            </BulkUploadActionContainer>
          )}
        </SectionHeading>
        {loadingGetThirdPartyFiles && !hasVisibleDocuments && <Loading />}
        {!loadingGetThirdPartyFiles && !hasVisibleDocuments && (
          <DocumentsPlaceholder>
            <DocumentsPlaceholderIcon />
            <div className="title">You've not added anything yet</div>
            <div>Add a document and it will be shown here</div>
          </DocumentsPlaceholder>
        )}
        {hasVisibleDocuments && (
          <>
            <div
              style={{
                display: 'flex',
                gap: '20px',
              }}
            >
              <RecordsSummary>
                <RecordsLabel>{visibleRecordsCount} records</RecordsLabel>
                {documentErrorCount > 0 && (
                  <DocumentErrorCount>
                    <span>·</span>
                    <Icon icon="alert" size="xxxs" aria-hidden />
                    <span>{documentErrorCount} errors</span>
                  </DocumentErrorCount>
                )}
              </RecordsSummary>
              {isMultiSelect && (
                <>
                  <Button
                    style={{ padding: '6px 0 0 0', minHeight: 0, height: 0 }}
                    small
                    text
                    title="select all"
                    disabled={isSelectAllDisabled}
                    onClick={handleSelectAllClick}
                    aria-label="Select all"
                  >
                    Select all
                  </Button>
                  <Button
                    style={{ padding: '6px 0 0 0', minHeight: 0, height: 0 }}
                    small
                    text
                    className="button cancel"
                    title="cancel"
                    onClick={disableMultiFileDownload}
                    aria-label="cancel"
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
            <DocumentsContainer>
              {filesInfo.map((fileInfo) => {
                const fileStatusUpdate = fileStatusUpdates[fileInfo.id];
                const fileStatus = fileStatusUpdate?.status ?? fileInfo.fileStatus;
                const errorMessage =
                  fileStatusUpdate?.errorMessage ??
                  (fileStatus?.toLowerCase() === 'error' ? 'Upload failed.' : undefined);

                return (
                  <DocumentCard
                    key={fileInfo.id}
                    fileType={
                      getFileExtension(fileInfo.fileDownloadName)?.toLowerCase() as FileFormat
                    }
                    name={decodedFileName(fileInfo.name)}
                    date={fileInfo.uploadedDate}
                    expiryDate={fileInfo.expiryDate}
                    category={fileInfo.category}
                    extendedNames={false}
                    downloadState={downloads[fileInfo.id]}
                    fileStatus={fileStatus}
                    errorMessage={errorMessage}
                    onAutoReset={() => resetDownload(fileInfo.id)}
                    actionComponent={
                      isMultiSelect ? (
                        <ActionComponentCheckbox id={fileInfo.id} />
                      ) : (
                        <ThreeDotsContextMenu
                          thirdPartyId={thirdPartyId}
                          fileInfo={fileInfo}
                          onEdit={() => {
                            setIsDocumentUploadModalOpen({
                              open: true,
                              documentId: fileInfo.id,
                            });
                          }}
                          onFailView={handleFailView}
                          onFailDownload={handleFailDownload}
                          onSuccessDelete={handleSuccessDelete}
                          onFailDelete={handleFailDelete}
                          showDeleteOption={canDeleteDocuments}
                          showEditOption={canEditDocuments}
                          onStartDownloadFile={startDownload}
                          onProgressDownloadFile={progressDownload}
                          onFailDowloadFile={failDowload}
                          onSuccessDownloadFile={successDownload}
                        />
                      )
                    }
                  />
                );
              })}
              {pendingDocumentUploads.map((pendingUpload) => (
                <DocumentCard
                  key={pendingUpload.uploadId}
                  fileType={
                    (pendingUpload.type ||
                      getFileExtension(pendingUpload.name)?.toLowerCase()) as FileFormat
                  }
                  name={pendingUpload.name}
                  fileSize={pendingUpload.size}
                  date={new Date().toString()}
                  showCategory={false}
                  downloadState={{
                    progress: pendingUpload.failed ? 0 : 1,
                    loading: !pendingUpload.failed,
                    failed: !!pendingUpload.failed,
                  }}
                  autoResetFailed={pendingUpload.failed ? false : undefined}
                  errorMessage={pendingUpload.errorMessage}
                  onAutoReset={() => removePendingDocumentUpload(pendingUpload.uploadId)}
                  actionComponent={
                    pendingUpload.failed ? (
                      <PendingUploadDeleteButton
                        type="button"
                        aria-label="Close"
                        onClick={() => removePendingDocumentUpload(pendingUpload.uploadId)}
                      >
                        <Icon icon="close" aria-hidden size="xxxxxs" cursor="pointer" />
                      </PendingUploadDeleteButton>
                    ) : undefined
                  }
                />
              ))}
            </DocumentsContainer>
            {cbiFilesInfo.length > 0 && (
              <>
                <CbiSectionHeader tag="h3" value="Interview video and transcript" />
                <DocumentsContainer style={{ paddingTop: '10px' }}>
                  {cbiFilesInfo.map((fileInfo) => (
                    <DocumentCard
                      fileSize={fileInfo.size}
                      key={fileInfo.id}
                      fileType={
                        getFileExtension(fileInfo.fileDownloadName)?.toLowerCase() as FileFormat
                      }
                      date={fileInfo.uploadedDate}
                      expiryDate={fileInfo.expiryDate}
                      category={fileInfo.category}
                      extendedNames={false}
                      name={fileInfo.name}
                      downloadState={downloads[fileInfo.id]}
                      onAutoReset={() => resetDownload(fileInfo.id)}
                      actionComponent={
                        isMultiSelect ? (
                          <ActionComponentCheckbox id={fileInfo.id} />
                        ) : (
                          <ThreeDotsContextMenu
                            thirdPartyId={thirdPartyId}
                            fileInfo={fileInfo}
                            onFailView={handleFailView}
                            onFailDownload={handleFailDownload}
                            onFailDelete={handleFailDelete}
                            showDeleteOption={canDeleteDocuments}
                            onStartDownloadFile={startDownload}
                            onProgressDownloadFile={progressDownload}
                            onFailDowloadFile={failDowload}
                            onSuccessDownloadFile={successDownload}
                            onSuccessDelete={handleSuccessDelete}
                          />
                        )
                      }
                    />
                  ))}
                </DocumentsContainer>
              </>
            )}
          </>
        )}
      </section>
      <DocumentUploadModal
        entityType={ThirdPartyType[type]}
        isOpen={isDocumentUploadModalOpen.open}
        thirdPartyId={thirdPartyId}
        linkToThirdParty
        notifyOnFileUpload
        onClose={() => setIsDocumentUploadModalOpen({ open: false, documentId: undefined })}
        onSuccessDocumentUpload={() => reloadGetThirdPartyFiles()}
        documentId={isDocumentUploadModalOpen.documentId}
        onSuccessDocumentEdit={() => {
          reloadGetThirdPartyFiles();
          setIsDocumentUploadModalOpen({ open: false, documentId: undefined });
        }}
      />
      <DeleteLinkedConfirmationModal
        isOpen={areConflictedFilesModalOpen.open}
        conflictedScreenings={areConflictedFilesModalOpen.conflictedScreenings}
        onClose={() =>
          setAreConflictedFilesModalOpen({
            open: false,
            conflictedScreenings: undefined,
          })
        }
      ></DeleteLinkedConfirmationModal>
      <ConfirmationModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        onProceed={() => fetchFileLists()}
        onCancel={disableMultiFileDownload}
        title="Download documents"
        messageBody={
          <>
            <Typography
              className="info-text"
              value="The selected documents will be delivered via email to "
              tag="span"
            />
            <Typography
              styles={{ fontWeight: 'bold' }}
              className="info-text"
              value={`${email}.`}
              tag="span"
            />
          </>
        }
        proceedButtonText="Confirm"
        proceedButtonLoading={loadingRequestFiles}
        onProceedAriaLabel="Confirm"
        loading={false}
        cancelButtonProps={{
          outline: false,
          text: true,
          'aria-label': 'Cancel',
        }}
      />
    </>
  );
};

export default Documents;
